import React, { useState, useEffect } from 'react';
import {
  Table,
  Card,
  Tag,
  Button,
  Modal,
  Form,
  Input,
  InputNumber,
  Space,
  Switch,
  Popconfirm,
  message,
} from 'antd';
import { PlusOutlined, EditOutlined, CheckOutlined, DeleteOutlined } from '@ant-design/icons';
import apiClient from '../services/api';

const Currencies = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 20, total: 0 });
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const fetchData = async (page = 1, pageSize = 20) => {
    setLoading(true);
    try {
      const res = await apiClient.get('/admin/currencies', {
        params: { page, per_page: pageSize },
      });
      const d = res.data;
      setData(d.data || []);
      setPagination((prev) => ({
        ...prev,
        current: d.current_page || page,
        total: d.total || 0,
      }));
    } catch {
      message.error('Failed to load currencies');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.setFieldsValue(record);
    setModalOpen(true);
  };

  const handleSave = async () => {
    const values = await form.validateFields();
    try {
      if (editing) {
        await apiClient.put(`/admin/currencies/${editing.id}`, values);
        message.success('Currency updated');
      } else {
        await apiClient.post('/admin/currencies', values);
        message.success('Currency created');
      }
      setModalOpen(false);
      fetchData(pagination.current);
    } catch {
      message.error('Failed to save currency');
    }
  };

  const handleSetDefault = async (id) => {
    try {
      await apiClient.post(`/admin/currencies/${id}/set-default`);
      message.success('Default currency updated');
      fetchData(pagination.current);
    } catch {
      message.error('Failed to set default');
    }
  };

  const handleToggleStatus = async (id) => {
    try {
      await apiClient.patch(`/admin/currencies/${id}/toggle-status`);
      fetchData(pagination.current);
    } catch {
      message.error('Failed to toggle status');
    }
  };

  const handleDelete = async (id) => {
    try {
      await apiClient.delete(`/admin/currencies/${id}`);
      message.success('Currency deleted');
      fetchData(pagination.current);
    } catch {
      message.error('Failed to delete currency');
    }
  };

  const columns = [
    { title: 'Code', dataIndex: 'code', key: 'code' },
    { title: 'Name', dataIndex: 'name', key: 'name' },
    { title: 'Symbol', dataIndex: 'symbol', key: 'symbol' },
    {
      title: 'Exchange Rate',
      dataIndex: 'exchange_rate',
      key: 'exchange_rate',
      render: (v) => v?.toFixed(6),
    },
    {
      title: 'Default',
      dataIndex: 'is_default',
      key: 'is_default',
      render: (v) => (v ? <Tag color="blue">Default</Tag> : null),
    },
    {
      title: 'Active',
      dataIndex: 'is_active',
      key: 'is_active',
      render: (v, r) => (
        <Switch checked={v} onChange={() => handleToggleStatus(r.id)} disabled={r.is_default} />
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, r) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} />
          {!r.is_default && (
            <Button size="small" icon={<CheckOutlined />} onClick={() => handleSetDefault(r.id)}>
              Set Default
            </Button>
          )}
          {!r.is_default && (
            <Popconfirm title="Delete this currency?" onConfirm={() => handleDelete(r.id)}>
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <Card
      title="Currencies"
      extra={
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Add Currency
        </Button>
      }
    >
      <Table
        columns={columns}
        dataSource={data}
        rowKey="id"
        loading={loading}
        pagination={{
          ...pagination,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (t) => `Total ${t} currencies`,
        }}
        onChange={(p) => fetchData(p.current, p.pageSize)}
      />
      <Modal
        title={editing ? 'Edit Currency' : 'Add Currency'}
        open={modalOpen}
        onOk={handleSave}
        onCancel={() => setModalOpen(false)}
        destroyOnClose
      >
        <Form form={form} layout="vertical">
          <Form.Item name="code" label="Code" rules={[{ required: true, max: 3 }]}>
            <Input maxLength={3} style={{ textTransform: 'uppercase' }} />
          </Form.Item>
          <Form.Item name="name" label="Name" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="symbol" label="Symbol" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="exchange_rate" label="Exchange Rate" rules={[{ required: true }]}>
            <InputNumber min={0.000001} step={0.01} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </Card>
  );
};

export default Currencies;
